import React, { useMemo } from 'react';
import {
  ActivityIndicator,
  Image,
  Pressable,
  RefreshControl,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from '../components/ui/Button';
import EmptyState from '../components/ui/EmptyState';
import Icon from '../components/ui/Icon';
import Skeleton from '../components/ui/Skeleton';
import Text from '../components/ui/Text';
import { Booking } from '../data/bookings';
import useWallet from '../hooks/useWallet';
import {
  colors,
  hairline,
  radii,
  screenPadding,
  shadows,
  spacing,
  useResponsive,
} from '../theme';

/**
 * The balance, and where the money in it actually came from.
 *
 * ---------------------------------------------------------------------------
 * THE BALANCE IS REAL NOW; THE REFUND HISTORY IS A DIFFERENT THING
 * ---------------------------------------------------------------------------
 * There IS a wallet ledger now - Homingo Coins, earned per completed booking
 * at a tier-dependent rate and spendable against a future one. The balance
 * above comes from `GET /customers/me/wallet` and moves on its own; the
 * seeded `homingoWallet` figure this screen used to draw is gone.
 *
 * The refund list below is still a separate fact and stays separate: every
 * refund is real money the platform has actually
 * returned, on a real booking, in the real amount `toAppBooking` already
 * carries - see `refundedAmount`/`cancellationFeeAmount` on `Booking`. A
 * refund is not literally a wallet credit today (it goes back to the original
 * payment method, not into this balance), so the two are shown as what they
 * are - a balance, and a history - rather than papering over the gap with a
 * total that would not add up.
 *
 * ---------------------------------------------------------------------------
 * NO SAMPLE ROWS
 * ---------------------------------------------------------------------------
 * There used to be a `PLACEHOLDER_REFUNDS` pair here - "Kitchen deep cleaning
 * +₹899", "Sofa and carpet shampoo +₹1,299" - shown whenever the real refund
 * list was empty, with a "Sample" tag on each. The tag was not enough. Signed
 * out, `bookings` is empty for the ordinary reason that nobody has asked the
 * server anything, so every guest opening this screen was shown ₹2,198 of
 * refunds against an account that does not exist, plus a "2 Refunds / All
 * Approved" strip totalling them. A figure with a caption reading "Total
 * refunded" is a claim about someone's money, and it was fiction.
 *
 * An empty wallet now says it is empty.
 *
 * ---------------------------------------------------------------------------
 * NO "THIS MONTH" AND NO LOYALTY ROW
 * ---------------------------------------------------------------------------
 * `Booking` carries no structured date - `when` is a display string like
 * "21 May, 9:00 AM" or "Completed on 4 Jun, 12:14 PM", already formatted and
 * not parseable back into a calendar month without guessing a year. A
 * month-scoped figure built on top of that would be a number this screen
 * cannot actually stand behind, so the stats strip only shows what the data
 * supports: a lifetime total and a count. Loyalty credits are the same
 * story - nothing in `data/` models one, so there is no row for it here.
 */

/** Whole rupees, grouped - `Intl` is not reliably present on Android JSC. */
const rupees = (value: number) =>
  '₹' + String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/**
 * One line in "Recent activity", whichever source it came from.
 *
 * Flattened to this shape at the point the two lists merge, so the renderer
 * below draws one kind of row rather than branching on where each came from.
 * `amount` carries its own unit - "+250 coins" or "+₹899" - because coins and
 * rupees must never be added together or shown in a shared column.
 */
type ActivityRow = {
  key: string;
  /** ISO date-time. Undefined only where the server sent none. */
  at?: string;
  title: string;
  credit: boolean;
  amount: string;
  sub: string;
  bookingId?: string;
};

/**
 * An ISO timestamp as a short, readable line - "4 Jun, 12:14 PM".
 *
 * The statement's `createdAt` is a real ISO date-time, unlike `Booking.when`
 * which is already a display string. That is what makes this possible here
 * and impossible there - see the note above on why the old stats strip had
 * no "this month" figure.
 *
 * Falls back to the raw string rather than throwing: a row with an unparseable
 * date should still show its reason and its coins, which are the parts that
 * matter.
 */
const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const formatWhen = (iso: string): string => {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) {
    return iso;
  }
  const hours = at.getHours();
  const suffix = hours >= 12 ? 'PM' : 'AM';
  /* 0 and 12 both read as 12 on a 12-hour clock. */
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  const minutes = String(at.getMinutes()).padStart(2, '0');
  return `${at.getDate()} ${MONTHS[at.getMonth()]}, ${hour12}:${minutes} ${suffix}`;
};

/**
 * Height of the banner before the balance card overlaps its lower edge.
 *
 * Close to the artwork's own aspect ratio (941×709, ≈1.33) at a typical
 * phone width, rather than a shorter number tuned by eye - a box much
 * shorter than the shot's own proportions forces `cover` to crop away more
 * of the picture than the composition has slack for, and the wallet on the
 * table read as cut off rather than simply cropped to a wide banner.
 */
const BANNER_HEIGHT = 300;
/** How far the balance card rises into the banner's own lower edge. */
const CARD_OVERLAP = 64;

type Props = {
  onBack: () => void;
  /** Every booking on file - refunds are read off whichever ones have one. */
  bookings: Booking[];
  onOpenBooking?: (booking: Booking) => void;
  /**
   * Opens the catalogue so the balance can actually be spent.
   *
   * The wallet has no withdrawal - the money only leaves it by paying for a
   * booking - so the one useful thing this screen can offer is the way to a
   * booking. Omitted where there is nowhere to send anyone, for instance in
   * a test.
   */
  onRedeem?: () => void;
};

function WalletScreen({ onBack, bookings, onOpenBooking, onRedeem }: Props) {
  /*
   * The balance and the coin statement, from the server.
   *
   * `data/payment.ts`'s `homingoWallet` seeded this from a saved instrument and
   * never moved - its own comment said wiring it to a server was what would
   * make it honest. This is that wiring. The tier, earn rate and expiry below
   * have no local equivalent at all; they exist only because the backend
   * computes them.
   */
  const coins = useWallet();
  const summary = coins.summary.data;

  const resp = useResponsive();
  const insets = useSafeAreaInsets();

  /*
   * The coin statement, which is what "Recent activity" is actually about.
   *
   * ---------------------------------------------------------------------------
   * WHY THIS IS NOT BUILT FROM `bookings` ANY MORE
   * ---------------------------------------------------------------------------
   * It was: the list filtered every booking carrying a `refundedAmount` and
   * drew those. That is a REFUND list wearing an activity heading, and it
   * silently dropped every other way coins move - a referral reward, the
   * coins earned on a completed booking, a redemption at checkout, an expiry.
   * Someone who referred a friend, watched their balance rise, and opened
   * this screen saw "No refunds yet", because a referral is not a refund and
   * no booking on file carried one.
   *
   * `useWallet` was already fetching the real statement from
   * `/customers/me/wallet/transactions` - every page of it, `loadMore` and
   * all - and the screen simply never rendered it. This is that list.
   */
  /*
   * Coin movements AND rupee refunds, in one list, newest first.
   *
   * ---------------------------------------------------------------------------
   * WHY BOTH, AND WHY THEY ARE DIFFERENT THINGS
   * ---------------------------------------------------------------------------
   * They live in different places and neither can stand in for the other.
   * Coins move through `/customers/me/wallet/transactions` - earned, redeemed,
   * expired, referred. A rupee refund never touches that ledger: the server
   * writes `refundedAmount` on the BOOKING and sends the money back to the
   * card or UPI it came from.
   *
   * Showing only one is what this screen has done twice, wrongly in both
   * directions. It drew refunds and missed every referral; then it drew coins
   * and dropped refunds a customer could plainly remember receiving. "Recent
   * activity" means everything that moved, so it is both.
   *
   * They are NOT summed anywhere. Coins and rupees are different units and a
   * combined total would be a fiction - each row carries its own.
   */
  const activity = useMemo(() => {
    const fromCoins: ActivityRow[] = coins.transactions.map(entry => ({
      key: `coin:${entry.id}`,
      at: entry.createdAt,
      title: entry.reason,
      credit: entry.coins >= 0,
      /* Coins are the unit here; the rupee value is what the server put on
         the movement, shown underneath. */
      amount: `${entry.coins >= 0 ? '+' : ''}${entry.coins} coins`,
      sub: `₹${entry.rupeeValue}`,
      bookingId: entry.bookingId ?? undefined,
    }));

    const fromRefunds: ActivityRow[] = bookings
      .filter(booking => booking.refundedAmount !== undefined)
      .map(booking => ({
        key: `refund:${booking.id}`,
        /* `cancelledAt` and not `when`: the latter is a display string with no
           year, so it cannot order anything. A refund with no timestamp still
           shows - it just sorts to the bottom rather than being dropped. */
        at: booking.cancelledAt,
        title: `Refund - ${booking.title}`,
        credit: true,
        amount: `+${rupees(booking.refundedAmount ?? 0)}`,
        sub: booking.reference,
        bookingId: booking.id,
      }));

    return [...fromCoins, ...fromRefunds].sort((a, b) => {
      /* Undated rows sit at the end rather than jumping to the top, which is
         where an empty string would sort them. */
      if (!a.at) {
        return 1;
      }
      if (!b.at) {
        return -1;
      }
      return a.at < b.at ? 1 : -1;
    });
  }, [coins.transactions, bookings]);

  const rows = activity;

  /*
   * Lifetime totals come off the SUMMARY, not off this page.
   *
   * The old figure summed `refundedAmount` across the rows on screen, so it
   * grew as `loadMore` fetched more and described "what has loaded" rather
   * than "what you have earned". The server already computes the real
   * lifetime numbers - see `WalletSummary` - so they are read, not derived.
   */
  const lifetimeEarned = summary?.lifetimeEarnedCoins ?? 0;
  const lifetimeRedeemed = summary?.lifetimeRedeemedCoins ?? 0;
  /*
   * Locked coins, kept deliberately apart from the balance above.
   *
   * The server does not count them in `balanceCoins` and neither does this
   * screen: adding them would put a figure on the card that the cart then
   * refuses to spend, which is the one thing worse than not showing them.
   */
  const pendingCoins = summary?.pendingVoucherCoins ?? 0;
  const pendingVouchers = summary?.pendingVouchers ?? [];

  return (
    <View style={styles.root}>
      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scroll}
        /* The same pull-down every list in the app answers - re-reads the
           balance and the statement together. */
        refreshControl={
          <RefreshControl
            refreshing={coins.summary.loading && !!summary}
            onRefresh={coins.reload}
            tintColor={colors.primary}
            colors={[colors.primary]}
          />
        }
      >
        {/* ------------------------------------------------------- banner */}
        <View style={styles.banner}>
          {/* Runs the full height of the frame, top edge included - nothing
              is cropped away to make room for the header below; the header
              floats over the artwork instead of cutting into it. */}
          <Image
            source={require('../../assets/uploads/banner/wallet.png')}
            style={styles.bannerArt}
            resizeMode="cover"
          />

          {/* ------------------------------------------------------ header */}
          <View style={[styles.header, { paddingTop: insets.top + spacing.sm }]}>
            {/* Holds the back chip's place. The chip itself is pinned outside
                the ScrollView, below, so it does not scroll away. */}
            <View style={styles.headerSpacer} />
            <Text variant="title" align="center" style={styles.headerTitle}>
              Wallet
            </Text>
            {/* Balances the back chip's width so the title centres on the
                row, without drawing a second, empty chip beside it. */}
            <View style={styles.headerSpacer} />
          </View>

          <View style={styles.bannerCopy}>
            <Text variant="title">Every clean,{'\n'}extra rewarding</Text>
          </View>
        </View>

        <View style={[styles.page, { paddingHorizontal: screenPadding + resp.sideInset }]}>
          {/* -------------------------------------------------- balance */}
          <View style={styles.balanceCard}>
            <View style={styles.balanceMark}>
              <Icon name="wallet" size={22} color={colors.primary} />
            </View>

            <View style={styles.balanceBody}>
              {/*
                Just the name. The tier used to be appended here as
                "Homingo Coins · silver", and it is still on the summary and
                still shown further down the page where it is explained - the
                tier row says what the tier earns and what reaches the next
                one. Up here it was a word with no context sitting on the one
                line that labels the balance.
              */}
              <Text variant="caption" color={colors.inkMuted}>
                Homingo Coins
              </Text>
              {/*
                The coin count leads, with its rupee value under it.

                Coins are what the server actually holds and what every reason
                string in the statement is denominated in; showing only rupees
                would leave "25 coins for booking HB-2026-000412" reading as a
                different currency from the balance above it.

                `balanceValue` is a decimal STRING off a Prisma column - it is
                rendered, never parsed. `rupees()` is not used here for that
                reason: it takes a number and would force a lossy round-trip.
              */}
              {/*
                Zero, not a dash, once the answer is actually known.

                `summary` is null in three different situations and the dash
                collapsed all of them: still loading, refused for want of a
                session, and a genuinely empty wallet. Only the last is worth
                a number, so the other two say what they are instead - a
                skeleton while it is in flight, and the sign-in line below
                when there is no account to have coins in.
              */}
              {coins.summary.loading ? (
                <Skeleton width={72} height={34} radius={radii.sm} />
              ) : (
                <Text variant="displaySm">{summary?.balanceCoins ?? 0}</Text>
              )}
              {/*
                No rupee-and-rate line under the figure.

                It read "₹452.00 · earning 5% back" - two different facts
                crammed under the balance, neither of them the balance. The
                rupee value is what the coins are worth when spent, which the
                cart states at the moment it matters, and the earn rate is a
                property of the tier, which the tier row below explains in
                full. Both are still on `summary`; neither is shown here.
              */}
              {!summary && !coins.signedIn && !coins.summary.loading ? (
                /* Says WHY it is zero. A bare 0 with nothing under it reads as
                   "you have earned nothing", which is a different claim. */
                <Text variant="caption" color={colors.inkMuted}>
                  Sign in to see your coins
                </Text>
              ) : null}
            </View>

            {/*
              The only way the money moves, in the space the figure leaves.

              There is no withdrawal - this balance is credit against a
              booking, not cash - so the honest call to action is the
              catalogue. Beside the amount rather than under it: the row is
              only as tall as the figure and the right half of it was empty,
              so a stacked button made the card taller for no reason.

              The explanatory line that sat under it is gone with the stack -
              there is no room for a sentence here, and "Redeem" against a
              balance already implies spending it. `accessibilityHint` carries
              what the line said, for anyone who needs it spelled out.

              Hidden on an empty wallet: a button offering to spend ₹0 is a
              dead end dressed as an action.
            */}
            {(summary?.balanceCoins ?? 0) > 0 && onRedeem ? (
              <Button
                label="Redeem now"
                /*
                 * `sm` (36pt), not `md` (44).
                 *
                 * It shares a row with the balance, which is the thing the
                 * card exists to show. At 44pt the button was nearly as tall
                 * as the figure beside it and read as the card's main event.
                 */
                size="sm"
                /* Hugs its label instead of stretching. `Button` defaults to
                   `fullWidth`, which inside a row would have it fight the
                   amount beside it for the same space. */
                fullWidth={false}
                onPress={onRedeem}
                accessibilityHint="Opens services. Your balance applies at checkout."
                style={styles.redeem}
              />
            ) : null}
          </View>

          {/* -------------------------------------------------- pending */}
          {/*
            Only when there are some. A permanent "0 coins on the way" card
            is the same mistake as the expiring-soon badge below it - see the
            note on `expiringSoonCoins`.
          */}
          {pendingCoins > 0 ? (
            <View style={styles.pendingCard}>
              <View style={styles.pendingHead}>
                <Text variant="subtitle">{pendingCoins} coins on the way</Text>
                <Text variant="labelSm" color={colors.inkMuted}>
                  LOCKED
                </Text>
              </View>
              <Text variant="caption" color={colors.inkMuted}>
                Left over from a voucher. They unlock once the service is done.
              </Text>
              {pendingVouchers.map(item => (
                <View key={item.redemptionId} style={styles.pendingRow}>
                  <Text variant="caption" color={colors.inkBody}>
                    {item.code ? `Voucher ${item.code}` : 'Voucher'} ·{' '}
                    {item.coins} coins
                  </Text>
                  {/* What they are waiting on, named - so "when?" has an
                      answer on the screen rather than in support. */}
                  <Text variant="caption" color={colors.inkMuted}>
                    {item.bookingNumbers.join(', ')}
                  </Text>
                </View>
              ))}
            </View>
          ) : null}

          {/* ---------------------------------------------------- stats */}
          {/*
            Numbers and words, nothing else.

            This card used to carry five icon discs - one on the heading and
            one beside each figure - plus an inner bordered card around the
            row. Design review called the page cluttered, and this card was
            most of why: every decoration was repeating something the words
            already said. The figures now stand on their own, separated by the
            same thin rules the account's stat strip uses.
          */}
          <View style={styles.statsCard}>
            <View>
              <Text variant="subtitle">Coins overview</Text>
              <Text variant="caption" color={colors.inkMuted}>
                {rows.length === 0
                  ? 'Coins you earn and spend show here.'
                  : 'Everything you have earned and redeemed.'}
              </Text>
            </View>

            <View style={styles.statsRow}>
              <View style={styles.stat}>
                {/* Off the summary, so it is a LIFETIME figure and does not
                    grow as more pages of the statement load. */}
                <Text variant="title">{lifetimeEarned}</Text>
                <Text variant="caption" color={colors.inkMuted}>
                  Coins earned
                </Text>
              </View>
              <View style={styles.statDivider} />
              <View style={styles.stat}>
                <Text variant="title">{lifetimeRedeemed}</Text>
                <Text variant="caption" color={colors.inkMuted}>
                  Coins redeemed
                </Text>
              </View>
              {/*
                Completed bookings, off the summary - the number the tier is
                actually computed from, so it is the one worth showing beside
                the two coin totals.
              */}
              <View style={styles.statDivider} />
              <View style={styles.stat}>
                <Text variant="title" numberOfLines={1}>
                  {summary?.completedBookings ?? 0}
                </Text>
                <Text variant="caption" color={colors.inkMuted} numberOfLines={1}>
                  Bookings
                </Text>
              </View>
            </View>
          </View>

          {/* ------------------------------------------------- activity */}
          <View style={styles.sectionHead}>
            <Text variant="subtitle">Recent activity</Text>
          </View>

          <View style={styles.list}>
            {coins.statement.loading && rows.length === 0 ? (
              /* A first load, not an empty wallet. Saying "nothing yet" while
                 the statement is still in flight is how someone who HAS coins
                 gets told they have none. */
              <ActivityIndicator color={colors.primary} style={styles.pending} />
            ) : rows.length === 0 ? (
              /*
                An honest empty state, where two invented refunds used to sit.

                `EmptyState` rather than a hand-rolled block, so this matches
                the bookings, cart and tickets screens - see the note on the
                component itself.
              */
              <EmptyState
                icon="refund"
                title={
                  coins.signedIn ? 'No activity yet' : 'Sign in to see your wallet'
                }
                message={
                  coins.signedIn
                    ? 'Coins you earn, refunds and anything you redeem show up here.'
                    : 'Your balance and coin history are tied to your account.'
                }
                compact
              />
            ) : (
              rows.map((entry: ActivityRow) => {
                /*
                 * A row names a booking, and sometimes that booking is one
                 * this screen was handed - then the row becomes the way to
                 * it. A referral reward, an expiry or a manual adjustment has
                 * none, and simply does not open anything rather than opening
                 * the wrong thing.
                 */
                const source = entry.bookingId
                  ? bookings.find(item => item.id === entry.bookingId)
                  : undefined;
                const open =
                  source && onOpenBooking
                    ? () => onOpenBooking(source)
                    : undefined;

                return (
                  <Pressable
                    key={entry.key}
                    accessibilityRole={open ? 'button' : undefined}
                    accessibilityLabel={`${entry.title}, ${entry.amount}`}
                    onPress={open}
                    style={({ pressed }) => [
                      styles.row,
                      pressed && open ? styles.pressed : null,
                    ]}
                  >
                    <View style={styles.rowMark}>
                      <Icon
                        name={entry.credit ? 'rupee' : 'wallet'}
                        size={19}
                        color={entry.credit ? colors.primary : colors.inkMuted}
                      />
                    </View>
                    <View style={styles.rowBody}>
                      {/* For a coin movement this is the backend's own
                          sentence, shown verbatim - it already names the
                          booking and the rate applied, so re-wording it here
                          would only make the two disagree. */}
                      <Text variant="subtitle" numberOfLines={2}>
                        {entry.title}
                      </Text>
                      <Text
                        variant="caption"
                        color={colors.inkMuted}
                        numberOfLines={1}
                      >
                        {entry.at ? formatWhen(entry.at) : entry.sub}
                      </Text>
                    </View>
                    <View style={styles.rowEnd}>
                      <Text
                        variant="label"
                        color={entry.credit ? colors.primary : colors.inkBody}
                      >
                        {entry.amount}
                      </Text>
                      {/* The second line only when the first did not already
                          use it - an undated row shows its reference above. */}
                      {entry.at ? (
                        <Text variant="caption" color={colors.inkMuted}>
                          {entry.sub}
                        </Text>
                      ) : null}
                    </View>
                  </Pressable>
                );
              })
            )}

            {/*
              The rest of the statement, on request.
              `useWallet` has paginated from the start - `loadMore`, `hasMore`
              and `loadingMore` were all there - and nothing ever called it, so
              the list silently stopped at the first 25 movements with no sign
              there were more. Shown only when the server is still handing back
              a cursor.
            */}
            {coins.hasMore ? (
              <Button
                label={coins.loadingMore ? 'Loading…' : 'Show older activity'}
                variant="secondary"
                onPress={coins.loadMore}
                disabled={coins.loadingMore}
                style={styles.loadMore}
              />
            ) : null}
          </View>

          {/* ---------------------------------------------------- footer */}
          {/* One quiet line, not a fourth card - part of the same declutter
              as the stats above. */}
          <View style={styles.footer}>
            <Icon name="shieldCheck" size={14} color={colors.inkMuted} />
            <Text variant="caption" color={colors.inkMuted}>
              Your transactions are encrypted and secure.
            </Text>
          </View>
        </View>
      </ScrollView>

      {/*
        Back, pinned over the corner rather than riding the banner.

        It was part of the header row above, and went up with the banner the
        moment the statement was scrolled - a long list with no way off it on an
        iPhone but to scroll all the way back. Laid over exactly the slot the
        spacer in that row keeps, so at rest it sits where it always did.
      */}
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Back"
        onPress={onBack}
        hitSlop={10}
        style={({ pressed }) => [
          styles.headerBack,
          styles.pinnedBack,
          { top: insets.top + spacing.sm },
          pressed ? styles.pressed : null,
        ]}
      >
        <Icon name="chevronLeft" size={22} color={colors.ink} />
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  /* Sits under the last row, inset like the rows themselves. */
  loadMore: {
    marginTop: spacing.md,
  },
  /* Sits where the first rows would, so the section does not collapse to
     nothing and then jump open when the statement lands. */
  pending: {
    paddingVertical: spacing.xl,
  },
  root: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scroll: {
    paddingBottom: spacing.xxxl,
  },

  /* ------------------------------------------------------------- header */
  /* Floats over the banner rather than sitting in its own opaque strip
     above it - that strip was what forced the artwork to be cropped or
     pushed down to leave room for it. Transparent, so the photograph shows
     through everywhere except the back button's own chip. */
  header: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: screenPadding,
    paddingBottom: spacing.sm,
    zIndex: 1,
  },
  headerBack: {
    width: 34,
    height: 34,
    borderRadius: radii.pill,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surface,
    ...shadows.sm,
  },
  headerSpacer: {
    width: 34,
    height: 34,
  },
  /* `top` is set at the render, off the safe-area inset. */
  pinnedBack: {
    position: 'absolute',
    left: screenPadding,
    zIndex: 1,
  },
  headerTitle: {
    flex: 1,
  },

  /* ------------------------------------------------------------- banner */
  banner: {
    height: BANNER_HEIGHT,
  },
  bannerArt: {
    position: 'absolute',
    width: '100%',
    height: '100%',
  },
  /* Sits in the banner's lower-left, above where the balance card overlaps
     the picture's bottom edge. */
  bannerCopy: {
    position: 'absolute',
    left: 0,
    right: 0,
    /* Lifted clear of the balance card. Measured from the banner's foot, so
       the card's overlap has to be cleared first and the headline sat close
       enough to look crowded by it - `xxl` puts air between the two. */
    bottom: CARD_OVERLAP + spacing.xxl,
    paddingHorizontal: screenPadding,
    gap: spacing.xs,
  },
  /* --------------------------------------------------------------- page */
  page: {
    /* Horizontal gutter is set inline from `resp.sideInset` - see the call
       site. A static `screenPadding` here was being clobbered by `resp.column`
       when the two were combined in one style array, which is what left the
       cards flush with the screen edge instead of gutter'd like the rest of
       the app. */
    paddingBottom: spacing.xxxl,
    /* Pulls everything below up into the banner's own lower edge. */
    marginTop: -CARD_OVERLAP,
    gap: spacing.lg,
  },
  /*
   * Nudged off the row's centre line.
   *
   * The row centres its children, which put the button level with the balance
   * figure's middle. Against a number that carries no descender the button
   * then read as sitting slightly high; a few points down lines it up with
   * the figure's baseline instead of its centre.
   */
  /*
   * Sits low against the balance, not level with the label above it.
   *
   * `alignSelf` overrides the card's own `alignItems: 'center'` for this child
   * alone - `marginTop` could not do it, because a centred item is positioned
   * from both edges and simply re-centres with the margin included. Ending the
   * button on the figure's baseline rather than the middle of the block is
   * what the design asks for, and `marginBottom` lifts it off the true bottom
   * so it does not look dropped.
   */
  redeem: {
    alignSelf: 'flex-end',
    marginBottom: spacing.xs,
  },
  /* One row: mark, amount, and the redeem button in the space to the right. */
  balanceCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.lg,
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    ...shadows.md,
  },
  balanceMark: {
    width: 48,
    height: 48,
    borderRadius: radii.md,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceAccent,
  },
  balanceBody: {
    flex: 1,
    gap: 2,
  },

  /* The whole overview - heading and every stat - is one card now, the
     same footprint as the balance card and the activity list below it. */
  /* Same card as the stats below it, so the locked coins read as part of
     the wallet rather than as a warning about it. */
  pendingCard: {
    padding: spacing.lg,
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    gap: spacing.sm,
    ...hairline,
  },
  pendingHead: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  pendingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  statsCard: {
    padding: spacing.lg,
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    gap: spacing.md,
    ...hairline,
  },
  /* A plain row of three figures, ruled apart - no inner card and no icon
     discs. See the render note on the declutter. */
  statsRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  stat: {
    flex: 1,
    alignItems: 'center',
    gap: 1,
  },
  statDivider: {
    width: 1,
    alignSelf: 'stretch',
    marginVertical: 2,
    backgroundColor: colors.border,
  },

  sectionHead: {
    marginTop: spacing.xs,
  },
  list: {
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    ...hairline,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.md,
    borderBottomWidth: hairline.borderWidth,
    borderBottomColor: hairline.borderColor,
  },
  rowMark: {
    width: 38,
    height: 38,
    borderRadius: radii.md,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceAccent,
  },
  rowBody: {
    flex: 1,
    gap: 2,
  },
  rowTitleLine: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  rowEnd: {
    alignItems: 'flex-end',
    gap: 2,
  },
  grow: {
    flex: 1,
  },
  sampleTag: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
    borderRadius: radii.sm,
    backgroundColor: colors.surfaceMuted,
  },

  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.sm,
  },

  pressed: {
    opacity: 0.6,
  },
});

export default WalletScreen;
